import { toast } from 'sonner';
import type { ExternalToast } from 'sonner';
import type { ReactNode } from 'react';

/**
 * 用户端通知统一入口（BUG-011）。
 *
 * 用户端所有面向家长的 Toast 通知都必须通过本封装触发（showUser* 系列），
 * 封装会给通知统一打上 `user-toast` 标记，由 `index.css` 中的样式在
 * 手机窄屏下将这类通知水平、垂直居中于当前可视区域。
 *
 * 管理员端（AdminLoginPage / AdminDashboardPage / TopicAdminPanel 及其
 * 上传、保存、删除、导入、调额、配置等操作）继续直接使用 `toast.*`，
 * 不携带该标记，位置与行为完全不变。
 *
 * 除位置外与 sonner 原生行为一致：自动消失、点击关闭、role=status
 * 无障碍语义、可传入任意 ExternalToast 配置（className 会被追加而非覆盖）。
 */

const USER_TOAST_CLASS = 'user-toast';

function withUserClass(options?: ExternalToast): ExternalToast {
  const existing = options?.className;
  const className = existing ? `${existing} ${USER_TOAST_CLASS}` : USER_TOAST_CLASS;
  return { ...options, className };
}

export function showUserToast(message: ReactNode, options?: ExternalToast): string | number {
  return toast(message, withUserClass(options));
}

export function showUserSuccess(message: ReactNode, options?: ExternalToast): string | number {
  return toast.success(message, withUserClass(options));
}

export function showUserError(message: ReactNode, options?: ExternalToast): string | number {
  return toast.error(message, withUserClass(options));
}

export function showUserInfo(message: ReactNode, options?: ExternalToast): string | number {
  return toast.info(message, withUserClass(options));
}

export function showUserWarning(message: ReactNode, options?: ExternalToast): string | number {
  return toast.warning(message, withUserClass(options));
}

export function showUserLoading(message: ReactNode, options?: ExternalToast): string | number {
  return toast.loading(message, withUserClass(options));
}
